import { LinkTextDecorationOptions } from "@amzn/stencil-react-components/link";
import { Table, TableColumn, TableSpacing } from "@amzn/stencil-react-components/table";
import { Text } from "@amzn/stencil-react-components/text";
import React, { FC } from "react";
import { InternalLink } from "../../../../common/components/InternalLink/internal-link";
import {
  deliveryMethodLabel,
  statusLabelText,
} from "../../../../common/helpers/order-display.util";
import { getString } from "../../../../common/uistringlabels/ui-string-utils";
import { Order } from "../../../../services/orders/orders.types";
import { HardwareOrderRow, toHardwareOrderRow } from "./hardware-order-row.mapper";
import {
  OrdersLoadingView,
  OrdersErrorState,
  OrdersEmptyState,
  buildDateComponent,
} from "./hardware-orders-table.shared";

const COLUMN_STRINGS = "orderDisplay.columns";
const EMPTY_VALUE = "-";

const OrderStatusCell: FC<{ row: HardwareOrderRow }> = ({ row }) => {
  const label = row.statusLabel
    ? statusLabelText(row.statusLabel)
    : getString("orderDisplay.unconfirmedStatus");
  return row.trackingPath ? (
    <InternalLink to={row.trackingPath} icon={null} textDecoration={LinkTextDecorationOptions.None}>
      {label}
    </InternalLink>
  ) : (
    <>{label}</>
  );
};

interface RowCell {
  data: HardwareOrderRow;
}

const tableColumns: TableColumn<HardwareOrderRow>[] = [
  {
    header: getString(`${COLUMN_STRINGS}.item`),
    accessor: ({ data }: RowCell) => (
      <Text fontSize="T200" color="neutral90" fontWeight={600}>
        {data.item ?? EMPTY_VALUE}
      </Text>
    ),
  },
  {
    header: getString(`${COLUMN_STRINGS}.order`),
    accessor: ({ data }: RowCell) => data.order ?? EMPTY_VALUE,
  },
  {
    header: getString(`${COLUMN_STRINGS}.ordered`),
    accessor: ({ data }: RowCell) => buildDateComponent(data.ordered),
  },
  {
    header: getString(`${COLUMN_STRINGS}.deliveryMethod`),
    accessor: ({ data }: RowCell) =>
      data.deliveryMethod ? deliveryMethodLabel(data.deliveryMethod) : EMPTY_VALUE,
  },
  {
    header: getString(`${COLUMN_STRINGS}.status`),
    accessor: ({ data }: RowCell) => <OrderStatusCell row={data} />,
  },
];

interface HardwareOrdersTableProps {
  orders: Order[];
  isLoading: boolean;
  hasError?: boolean;
  onRetry?: () => void;
}

export const HardwareOrdersTable: FC<HardwareOrdersTableProps> = ({
  orders,
  isLoading,
  hasError = false,
  onRetry,
}) => {
  if (isLoading) {
    return <OrdersLoadingView />;
  }

  if (hasError) {
    return <OrdersErrorState onRetry={onRetry} />;
  }

  if (orders.length === 0) {
    return <OrdersEmptyState />;
  }

  return (
    <Table
      columns={tableColumns}
      data={orders.map(toHardwareOrderRow)}
      spacing={TableSpacing.Default}
    />
  );
};

export default HardwareOrdersTable;
